import { useNavigate } from '@tanstack/react-router';
import { useCompanySwitcher } from '@/hooks/useCompanySwitcher';
import { useTranslator } from '@/hooks/useTranslator';
import { isImpersonating } from '@/lib/utils/managementAccess';
import { endImpersonation, logout } from '@/lib/utils/session';
import { useSessionStore } from '@/store/sessionStore';

/**
 * What the management user menu offers: who you are, the company you are acting
 * for, the way out of an impersonation, and sign out.
 *
 * Short by design — AI credits, the support chat and the self-service account
 * entries belong to the customer app. Company switching does not: an operator
 * with delegated access to several companies can switch between them from the
 * old dashboard's profile dropdown on any page, management included, and
 * leaving it out here meant the only way to change company was to cross back
 * over.
 */
export function useManagementUserMenu() {
  const user = useSessionStore(s => s.user);
  const navigate = useNavigate();
  const { getTranslation } = useTranslator({ page: 'appHeader' });

  // Into the company just chosen, as that company — not back into the tools.
  //
  // The old dashboard sent an admin to its impersonation picker here
  // (`ProfileDropdown.jsx`: `isAdmin() ? navigate('/management/impersonate')`),
  // and mirroring that made the click read as "took me to the impersonate page
  // and changed nothing": the switch had happened — `apiFetch` sends the new
  // `x-retraced-company-id` from that moment on — but `/management` shows no
  // company data, so nothing on screen reflected it.
  //
  // Landing on `/dashboard` instead is not enough on its own: a company
  // override leaves the session a staff session, and the customer area turns
  // staff away. `selfImpersonate` makes it the operator acting *as themselves
  // inside that company*, which is what delegated access grants them.
  const switcher = useCompanySwitcher({ selfImpersonate: true });

  return {
    getTranslation,
    companyId: user?.company?.id,
    search: switcher.search,
    setSearch: switcher.setSearch,
    companies: switcher.companies,
    hasCompanies: switcher.hasCompanies,
    handleSwitchCompany: switcher.handleSwitchCompany,
    onViewCompanyProfile: (companyId: string) =>
      void navigate({ to: '/network/$companyId', params: { companyId } }),
    // Reachable while impersonating only when the operator got here before the
    // switch — the area's guard turns them away otherwise. Kept so the way out
    // is never further than the menu.
    isImpersonating: isImpersonating(user),
    onEndImpersonation: endImpersonation,
    onLogout: () => void logout(),
  };
}
